import { LanguageHelperService } from './../../services/language.service';
import {
  Component,
  DestroyRef,
  ElementRef,
  inject,
  OnInit,
  computed,
  signal,
  viewChildren,
  WritableSignal
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TopMenuComponent } from '../shared/top-menu/top-menu.component';
import { MatMenuModule } from '@angular/material/menu';
import { MatIconModule } from '@angular/material/icon';
import {
  FEATURES,
  FEEDBACK_TYPES,
  PLATFORMS,
  SATISFACTION_LEVELS,
} from '../../common/constants';
import {
  FeedbackDTO,
  FeedbackRequestBodyDTO,
} from '../../dto/common-dto';
import { CommonModule } from '@angular/common';
import { TranslatePipe } from '@ngx-translate/core';
import { catchError } from 'rxjs';
import { FeedbackService } from '../../services/feedback.service';
import { FormsModule } from '@angular/forms';
// Temporarily unused while the feedback form is hidden behind the interim notice.
// import { MatTooltip } from '@angular/material/tooltip';
import { MatSnackBar } from '@angular/material/snack-bar';

/**
 * This component holds the logic for the feedback form.
 */
@Component({
    selector: 'app-modules-contact',
    imports: [
        TopMenuComponent,
        MatMenuModule,
        MatIconModule,
        CommonModule,
        TranslatePipe,
        FormsModule,
        // MatTooltip,
    ],
    standalone: true,
    templateUrl: './feedback.component.html',
    styleUrl: './feedback.component.scss'
})
export class FeedbackComponent implements OnInit {
  // Inject FeedbackService.
  private readonly feedbackService = inject(FeedbackService);
  // Inject LanguageHelperService.
  private readonly languageHelperService = inject(LanguageHelperService);
  // Inject DestroyRef.
  private readonly destroyRef = inject(DestroyRef);
  // Injects the MatSnackBar service to display snack-bar notifications.
  private readonly snackBar = inject(MatSnackBar);

  readonly feedbackSections = viewChildren<ElementRef>('feedbackSection');
  // Stores the feedback types.
  readonly feedbackTypes = signal<FeedbackDTO[]>([]);
  // Stores the satisfaction levels.
  readonly satisfactionLevels = signal<FeedbackDTO[]>([]);
  // Stores the features.
  readonly features = signal<FeedbackDTO[]>([]);
  // Stores the default platforms.
  readonly platforms = signal<FeedbackDTO[]>([]);
  // Stores the selected platforms.
  readonly selectedPlatforms = computed(() =>
    this.platforms().filter((el) => el.selected)
  );
  // Stores the selected feedback type.
  readonly selectedFeedbackType = computed(() =>
    this.feedbackTypes().find((el) => el.selected)
  );
  // Stores the selected satisfaction level.
  readonly selectedSatisfactionLevel = computed(() =>
    this.satisfactionLevels().find((el) => el.selected)
  );
  // Stores the selected features.
  readonly selectedFeatures = computed(() =>
    this.features().filter((el) => el.selected)
  );
  // Stores the suggestion.
  readonly suggestion = signal('');
  // Stores the email.
  readonly email = signal('');
  // Stores the phone number.
  readonly phoneNumber = signal<number | null>(null);
  // Tells if the required fields have been validated since the last reset.
  private readonly validated = signal(false);
  // Stores the names of the invalid sections.
  readonly invalidSections = computed(() => {
    const validated = this.validated();
    return {
      platform: validated && !this.selectedPlatforms().length,
      feedbackType: validated && !this.selectedFeedbackType(),
      satisfaction: validated && !this.selectedSatisfactionLevel(),
      features: validated && !this.selectedFeatures().length,
    };
  });
  // Tells if the email is valid or not.
  readonly isEmailValid = signal(true);

  /**
   * A lifecycle hook that is called after Angular has initialized all data-bound properties of a directive.
   */
  ngOnInit(): void {
    this.mapFeedbackTypes();
  }

  /**
   * Maps the feedback types.
   */
  mapFeedbackTypes(): void {
    const toFeedbackDTO = (el: { type: string; icon: string }): FeedbackDTO => ({
      type: el.type,
      icon: el.icon,
      selected: false,
    });

    this.platforms.set(PLATFORMS.map(toFeedbackDTO));
    this.feedbackTypes.set(FEEDBACK_TYPES.map(toFeedbackDTO));
    this.satisfactionLevels.set(SATISFACTION_LEVELS.map(toFeedbackDTO));
    this.features.set(FEATURES.map(toFeedbackDTO));
  }

  /**
   * Selects the platforms.
   *
   * @param platform The selected platform.
   */
  selectPlatform(platform: FeedbackDTO): void {
    this.toggleSelection(this.platforms, platform);
  }

  /**
   * Sets the feedback type.
   *
   * @param item The selected feedback type.
   */
  setFeedbackType(item: FeedbackDTO): void {
    this.selectOnly(this.feedbackTypes, item);
  }

  /**
   * Sets the satisfaction level.
   *
   * @param item The selected satisfaction level.
   */
  setLevel(item: FeedbackDTO): void {
    this.selectOnly(this.satisfactionLevels, item);
  }

  /**
   * Sets the features.
   *
   * @param item The selected feature.
   */
  setFeatures(item: FeedbackDTO): void {
    this.toggleSelection(this.features, item);
  }

  /**
   * Toggles the selected state of one item in a multi-select list.
   *
   * @param list The list holding the item.
   * @param item The item to toggle.
   */
  private toggleSelection(list: WritableSignal<FeedbackDTO[]>, item: FeedbackDTO): void {
    list.update((items) =>
      items.map((el) =>
        el.type === item.type ? { ...el, selected: !el.selected } : el
      )
    );
  }

  /**
   * Selects one item in a single-select list and deselects the others.
   *
   * @param list The list holding the item.
   * @param item The item to select.
   */
  private selectOnly(list: WritableSignal<FeedbackDTO[]>, item: FeedbackDTO): void {
    list.update((items) =>
      items.map((el) => ({ ...el, selected: el.type === item.type }))
    );
  }

  /**
   * Sends the feedback.
   */
  sendFeedback(): void {
    if (this.validateRequiredFields()) {
      const body: FeedbackRequestBodyDTO = {
        platform: this.selectedPlatforms().map((el) => el.type),
        feedbackType: this.selectedFeedbackType()?.type || '',
        satisfactionLevel: this.selectedSatisfactionLevel()?.type || '',
        features: this.selectedFeatures().map((el) => el.type),
        suggestion: this.suggestion(),
        email: this.email(),
        phoneNumber: this.phoneNumber() ?? 0,
      };
      this.feedbackService
        .sendFeedback(body)
        .pipe(
          takeUntilDestroyed(this.destroyRef),
          catchError((error) => {
            this.openSnackBar(
              this.languageHelperService.translations()['translateSomethingWentWrong'],
              'OK',
              'error'
            );
            throw error;
          })
        )
        .subscribe(() => {
          this.resetFields();
          this.openSnackBar(
            this.languageHelperService.translations()['translateFeedbackSentSuccessfuly'],
            'OK',
            'success'
          );
        });
    }
  }

  /**
   * Validates the required fields.
   *
   * @returns True if the fields are valid, false otherwise.
   */
  validateRequiredFields(): boolean {
    this.validated.set(false);

    this.isEmailValid.set(this.validateEmail(this.email()));
    if (!this.isEmailValid()) {
      return false;
    }

    this.validated.set(true);
    const invalidSections = Object.values(this.invalidSections());
    const hasInvalidFields = invalidSections.some((invalid) => invalid);

    if (hasInvalidFields) {
      const firstInvalidSection = this.feedbackSections().find(
        (section, index) => invalidSections[index]
      );

      if (firstInvalidSection) {
        // Scroll to the first invalid section.
        firstInvalidSection.nativeElement.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });
      }
      return false;
    }

    return true;
  }

  /**
   * Validates the email format.
   *
   * @param email Represents the email to validate.
   *
   * @returns True if the email is valid, false otherwise.
   */
  private validateEmail(email: string): boolean {
    if (!email) return true; // Empty email is valid since it's optional.
    const emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return emailPattern.test(email);
  }

  /**
   * Reset all the fields to default values.
   */
  resetFields(): void {
    const deselectAll = (items: FeedbackDTO[]): FeedbackDTO[] =>
      items.map((el) => ({ ...el, selected: false }));

    this.platforms.update(deselectAll);
    this.feedbackTypes.update(deselectAll);
    this.satisfactionLevels.update(deselectAll);
    this.features.update(deselectAll);
    this.suggestion.set('');
    this.phoneNumber.set(null);
    this.email.set('');
    this.validated.set(false);
  }

  /**
   * Opens the snackbar to inform the user about the result of the operation.
   *
   * @param message Represents the message to display.
   * @param action Represents the action to display.
   * @param type Represents the type of the snackbar.
   */
  openSnackBar(
    message: string,
    action: string,
    type: 'success' | 'error'
  ): void {
    this.snackBar.open(message, action, { duration: 3000, panelClass: [type] });
  }
}
